import * as React from 'react';
import styled, { useTheme } from 'styled-components';
import StyledSvg from '@sdk/components/ui/StyledSvg';
import ShiftBoxIcon from '@sdk/shifts/components/ShiftBoxIcon';
import useAuth from '@sdk/authentication/useAuth';
import useConfig from '@sdk/config/useConfig';
import {
  isShiftPendingAcceptance,
  isShiftRejected,
} from '@sdk/shiftAcceptance/helpers/shiftAcceptanceUtils';
import EventBoxOuter from '@sdk/schedule/components/EventBoxOuter';
import EventBoxContainerStyle, {
  EventBoxContainerProps,
} from '@sdk/schedule/components/EventBoxContainerStyle';
import EventBoxInner from '@sdk/schedule/components/EventBoxInner';
import {
  EventBoxCommonBaseProps,
  EventBoxSelectableProps,
} from '@sdk/schedule/types/EventBoxProps';
import AcceptanceStatusIndicator from '@sdk/shiftAcceptance/components/AcceptanceStatusIndicator';
import type { ShiftType } from '@sdk/shifts/types/ShiftType';
import type { ColorPalette } from '@sdk/theme/colorsLight';

import OpenEndIconWhite from '../assets/svg/Icons/16/shift-oe-white.svg';

import type { OpenEventCallback, IsShiftLineVisibleType } from './types';
import { EventBoxColorsType } from './types/EventBoxColorsType';
import FlexRow from './ui/FlexRow';
import { EVENT_TEXT_LINE_HEIGHT } from './windowed/constants';
import EventBoxSelect from './components/EventBoxSelect';
import getRenderBoxLine from './helpers/getRenderBoxLine';

type BoxProps = {
  bgHover: string;
  borderColorHover: string;
  hasAcceptanceStatus?: boolean;
  opacity: number;
} & EventBoxContainerProps;

const Box = styled(FlexRow)<BoxProps>`
  ${EventBoxContainerStyle}

  position: relative;
  border-color: ${({ borderColor, theme, hasAcceptanceStatus }): string =>
    hasAcceptanceStatus ? theme.colors.white : borderColor};
  opacity: ${({ opacity }): number => opacity};

  ${EventBoxOuter}:hover & {
    background-color: ${({ bgHover }): string => bgHover};
    border-color: ${({ borderColorHover, theme, hasAcceptanceStatus }): string =>
      hasAcceptanceStatus ? theme.colors.white : borderColorHover};
  }
`;

const OpenEndIconContainer = styled.div`
  height: ${EVENT_TEXT_LINE_HEIGHT}px;
`;

type Props = {
  shift: ShiftType;
  openEvent: OpenEventCallback;
  isExchange: boolean;
  isShiftLineVisible: IsShiftLineVisibleType;
  isHovered: boolean;
} & EventBoxCommonBaseProps &
  EventBoxSelectableProps;

const ShiftBox: React.FC<React.PropsWithChildren<Props>> = ({
  shift,
  openEvent,
  shiftAppearanceMode,
  hoverComponent,
  isSelected,
  showSelect,
  toggleSelection,
  isExchange,
  height,
  showPlus,
  isHovered,
}) => {
  const { currentUser } = useAuth();
  const config = useConfig();
  const { colors } = useTheme();

  const isPending =
    config.FEATURE_FLAGS.SHIFT_ACCEPTANCE && isShiftPendingAcceptance(shift, currentUser);
  const isRejected = config.FEATURE_FLAGS.SHIFT_ACCEPTANCE && isShiftRejected(shift, currentUser);
  const hasAcceptanceStatus = isPending || isRejected;

  const { position, status } = shift;
  const isPublished = status === 'published';
  const { bg, bgHover, borderColor, borderColorHover, textColor, textColorHover } = getColors(
    shift,
    colors
  );
  const borderStyle = isPublished ? 'solid' : 'dashed';
  const iconColor = isPublished ? colors.white : position?.color;

  const onClick = (evt: React.MouseEvent<HTMLDivElement>): void => {
    openEvent({ event: shift, isExchange, $event: evt });
  };

  const renderBoxLine = getRenderBoxLine({
    color: textColor,
    hoverColor: textColorHover,
    hoverComponent: EventBoxOuter,
  });

  const shiftAppearance = shift.shiftAppearance?.[shiftAppearanceMode];
  const opacity =
    shift.user && (!shift.user.active || shift.user.deleted) && shift.isPast ? 0.6 : 1;

  return (
    <EventBoxOuter>
      <AcceptanceStatusIndicator isPending={isPending} isRejected={isRejected} />
      <Box
        height={height}
        opacity={opacity}
        hoverComponent={hoverComponent}
        bg={bg}
        bgHover={bgHover}
        borderStyle={borderStyle}
        borderColor={borderColor}
        borderColorHover={borderColorHover}
        onClick={onClick}
        showPlus={showPlus}
        hasAcceptanceStatus={hasAcceptanceStatus}>
        {(isHovered || isSelected || showSelect) && shift.canBeSelected && !shift.foreign && (
          <EventBoxSelect
            isSelected={isSelected}
            toggleSelection={toggleSelection}
            showSelect={showSelect}
            hoverComponent={EventBoxOuter}
          />
        )}
        <EventBoxInner>
          {shiftAppearance?.firstLine && (
            <FlexRow>
              {renderBoxLine(shiftAppearance.firstLine, true)}
              {shift.openEnd && (
                <OpenEndIconContainer>
                  <StyledSvg svg={OpenEndIconWhite} color={iconColor} />
                </OpenEndIconContainer>
              )}
            </FlexRow>
          )}

          {shiftAppearance?.secondLine && renderBoxLine(shiftAppearance.secondLine)}
          {shiftAppearance?.thirdLine && renderBoxLine(shiftAppearance.thirdLine)}
          {shiftAppearance?.fourthLine && renderBoxLine(shiftAppearance.fourthLine)}
        </EventBoxInner>
        <ShiftBoxIcon
          currentUser={currentUser}
          shift={shift}
          bgColor={isPublished ? null : shift.position.color}
        />
      </Box>
    </EventBoxOuter>
  );
};

function getColors(shift: ShiftType, colors: ColorPalette): EventBoxColorsType {
  const { status, position, foreign } = shift;
  const isPublished = status === 'published';

  if (isPublished) {
    return {
      bg: foreign ? colors.unavailability : position.color,
      bgHover: position.color,
      borderColor: foreign ? colors.unavailability : position.color,
      borderColorHover: position.color,
      textColor: foreign ? colors.primaryText : colors.white,
      textColorHover: colors.white,
    };
  }

  return {
    bg: 'transparent',
    bgHover: 'transparent',
    borderColor: foreign ? colors.unavailability : position.color,
    borderColorHover: position.color,
    textColor: foreign ? colors.primaryText : position.color,
    textColorHover: position.color,
  };
}

function areEqual(prevProps: Props, nextProps: Props): boolean {
  const {
    displayLabel: prevDisplayLabel,
    position: prevPosition,
    location: prevLocation,
    status: prevStatus,
    acceptance: prevAcceptance,
    summary: prevSummary,
  } = prevProps.shift;
  const { name: prevPositionName, color: prevPositionColor } = prevPosition;
  const { name: prevLocationName } = prevLocation;
  const {
    isSelected: prevIsSelected,
    showSelect: prevShowSelect,
    isHovered: prevIsHovered,
  } = prevProps;

  const { isSelected, showSelect, isHovered, shift } = nextProps;
  const { displayLabel, position, location, status, acceptance, summary } = shift;
  const { name: positionName, color: positionColor } = position;
  const { name: locationName } = location;

  return (
    prevDisplayLabel === displayLabel &&
    prevPositionName === positionName &&
    prevPositionColor === positionColor &&
    prevLocationName === locationName &&
    prevStatus === status &&
    isSelected === prevIsSelected &&
    showSelect === prevShowSelect &&
    prevAcceptance === acceptance &&
    summary === prevSummary &&
    isHovered === prevIsHovered
  );
}

export default React.memo(ShiftBox, areEqual);
